import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import type { ComponentType } from 'react';
import { RootLayout } from '@/components/layout/RootLayout';

/** Adapts a conventional `export default` page module to react-router's `lazy` { Component } contract. */
function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  return async () => {
    const { default: Component } = await load();
    return { Component };
  };
}

/** `lazy` keeps each page in its own chunk — matches the spec's route-based code-splitting requirement. */
const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, lazy: lazyPage(() => import('@/pages/HomePage')) },
      { path: 'about', lazy: lazyPage(() => import('@/pages/AboutPage')) },
      { path: 'services', lazy: lazyPage(() => import('@/pages/services/ServicesListPage')) },
      {
        path: 'services/:slug',
        lazy: lazyPage(() => import('@/pages/services/ServiceDetailPage')),
      },
      { path: 'portfolio', lazy: lazyPage(() => import('@/pages/portfolio/PortfolioListPage')) },
      {
        path: 'portfolio/:slug',
        lazy: lazyPage(() => import('@/pages/portfolio/PortfolioDetailPage')),
      },
      { path: 'careers', lazy: lazyPage(() => import('@/pages/CareersPage')) },
      { path: 'blog', lazy: lazyPage(() => import('@/pages/blog/BlogListPage')) },
      { path: 'blog/:slug', lazy: lazyPage(() => import('@/pages/blog/BlogDetailPage')) },
      { path: 'contact', lazy: lazyPage(() => import('@/pages/ContactPage')) },
      { path: 'privacy-policy', lazy: lazyPage(() => import('@/pages/legal/PrivacyPolicyPage')) },
      { path: 'terms-of-service', lazy: lazyPage(() => import('@/pages/legal/TermsPage')) },
      { path: '*', lazy: lazyPage(() => import('@/pages/NotFoundPage')) },
    ],
  },
];

export const router = createBrowserRouter(routes);
